import type {
  ActivityRecord,
  ExitManagementAction,
  ExitManagementState,
  ExitProject,
  ExitTransaction,
} from './types';

const now = () => new Date().toISOString().slice(0, 16).replace('T', ' ');
const activity = (
  title: string,
  entityType: ActivityRecord['entityType'],
  entityId: string,
): ActivityRecord => ({
  id: `activity-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  title,
  actor: '当前用户',
  createdAt: now(),
  entityType,
  entityId,
});

export const exitManagementReducer = (
  state: ExitManagementState,
  action: ExitManagementAction,
): ExitManagementState => {
  switch (action.type) {
    case 'IMPORT_STATE':
      return JSON.parse(JSON.stringify(action.state)) as ExitManagementState;
    case 'UPDATE_OPPORTUNITY': {
      const current = state.opportunities[action.opportunityId];
      return {
        ...state,
        opportunities: {
          ...state.opportunities,
          [action.opportunityId]: {
            ...current,
            ...action.changes,
            updatedAt: now(),
          },
        },
        activities: [
          activity('更新退出机会判断', 'opportunity', action.opportunityId),
          ...state.activities,
        ],
      };
    }
    case 'CONVERT_OPPORTUNITY_TO_PROJECT': {
      const opportunity = state.opportunities[action.opportunityId];
      if (!opportunity) throw new Error('退出机会不存在');
      const company = state.companies[opportunity.companyId];
      const key = opportunity.companyId.replace('company-', '');
      const projectId = key === 'ruisi' ? 'project-ruisi' : `project-${key}`;
      const existing = state.projects[projectId];
      const project: ExitProject = {
        id: projectId,
        opportunityId: opportunity.id,
        companyId: opportunity.companyId,
        fundId: opportunity.fundId,
        name: existing?.name ?? `${company.shortName}退出项目`,
        owner: opportunity.owner,
        stage: '方案制定',
        currentHoldingRatio: opportunity.currentHoldingRatio,
        investmentCost: opportunity.investmentCost,
        latestValuation: company.latestValuation,
        riskLevel: opportunity.priority,
        plan: existing?.plan ?? {
          method: opportunity.recommendedMethod,
          alternatives: ['股权转让', 'S基金'],
          exitType: '部分退出',
          targetSellRatio: opportunity.currentHoldingRatio,
          targetValuation: company.latestValuation,
          minimumValuation: null,
          targetCompletionDate: '',
          reason: opportunity.judgmentConclusion,
          targetExitAmount: opportunity.expectedExitAmount,
        },
        nextAction: '完善退出方案',
      };
      return {
        ...state,
        opportunities: {
          ...state.opportunities,
          [opportunity.id]: {
            ...opportunity,
            status: '已转退出项目',
            updatedAt: now(),
          },
        },
        projects: { ...state.projects, [projectId]: project },
        activities: [
          activity(
            `${company.shortName}退出机会已转为退出项目`,
            'project',
            projectId,
          ),
          ...state.activities,
        ],
      };
    }
    case 'UPDATE_PROJECT':
      return {
        ...state,
        projects: {
          ...state.projects,
          [action.projectId]: {
            ...state.projects[action.projectId],
            ...action.changes,
          },
        },
        activities: [
          activity('更新退出项目', 'project', action.projectId),
          ...state.activities,
        ],
      };
    case 'UPDATE_PLAN': {
      const current = state.projects[action.projectId];
      return {
        ...state,
        projects: {
          ...state.projects,
          [action.projectId]: {
            ...current,
            plan: { ...current.plan, ...action.changes },
          },
        },
        activities: [
          activity('更新退出方案', 'project', action.projectId),
          ...state.activities,
        ],
      };
    }
    case 'ADD_BUYER':
      return {
        ...state,
        buyers: { ...state.buyers, [action.buyer.id]: action.buyer },
        activities: [
          activity(
            `新增候选买方 ${action.buyer.name}`,
            'project',
            action.buyer.projectId,
          ),
          ...state.activities,
        ],
      };
    case 'UPDATE_BUYER': {
      const buyer = state.buyers[action.buyerId];
      return {
        ...state,
        buyers: {
          ...state.buyers,
          [action.buyerId]: { ...buyer, ...action.changes },
        },
        activities: [
          activity(`更新买方 ${buyer.name}`, 'project', buyer.projectId),
          ...state.activities,
        ],
      };
    }
    case 'CREATE_TRANSACTION_FROM_BUYER': {
      const project = state.projects[action.projectId];
      const buyer = state.buyers[action.buyerId];
      if (buyer.stage !== '入选交易对手')
        throw new Error('请先将买方标记为入选交易对手');
      const key = project.companyId.replace('company-', '');
      const transactionId =
        key === 'ruisi'
          ? 'transaction-ruisi'
          : `transaction-${key}-${Date.now()}`;
      const transaction: ExitTransaction = {
        id: transactionId,
        projectId: project.id,
        companyId: project.companyId,
        buyerId: buyer.id,
        buyerName: buyer.name,
        name: `${project.name}—${buyer.name}`,
        seller: state.funds[project.fundId].name,
        stage: '交易准备',
        soldRatio: buyer.purchaseRatio,
        consideration:
          buyer.latestQuote ??
          Math.round((project.plan.targetValuation * buyer.purchaseRatio) / 100),
        enterpriseValuation: project.plan.targetValuation,
        agreementStatus: '未开始',
        owner: project.owner,
        expectedClosingDate: project.plan.targetCompletionDate,
        riskLevel: project.riskLevel,
      };
      return {
        ...state,
        projects: {
          ...state.projects,
          [project.id]: { ...project, stage: '交易执行' },
        },
        transactions: { ...state.transactions, [transactionId]: transaction },
        activities: [
          activity(
            `已创建退出交易：${buyer.name}`,
            'transaction',
            transactionId,
          ),
          ...state.activities,
        ],
      };
    }
    case 'UPDATE_TRANSACTION':
      return {
        ...state,
        transactions: {
          ...state.transactions,
          [action.transactionId]: {
            ...state.transactions[action.transactionId],
            ...action.changes,
          },
        },
        activities: [
          activity('更新退出交易', 'transaction', action.transactionId),
          ...state.activities,
        ],
      };
    case 'UPDATE_CONDITION':
      return {
        ...state,
        conditions: {
          ...state.conditions,
          [action.conditionId]: {
            ...state.conditions[action.conditionId],
            ...action.changes,
          },
        },
        activities: [
          activity(
            '更新先决条件',
            'transaction',
            state.conditions[action.conditionId].transactionId,
          ),
          ...state.activities,
        ],
      };
    case 'COMPLETE_CLOSING': {
      const transaction = state.transactions[action.transactionId];
      const project = state.projects[transaction.projectId];
      const required = Object.values(state.conditions).filter(
        (item) => item.transactionId === transaction.id && item.required,
      );
      if (
        required.some((item) => !['已完成', '已豁免'].includes(item.status))
      ) {
        throw new Error('仍有必要先决条件未完成');
      }
      const remaining = Math.max(
        0,
        project.currentHoldingRatio - action.soldRatio,
      );
      const company = state.companies[project.companyId];
      return {
        ...state,
        companies: {
          ...state.companies,
          [company.id]: {
            ...company,
            postInvestmentStatus: remaining === 0 ? '已退出' : '退出推进中',
          },
        },
        projects: {
          ...state.projects,
          [project.id]: {
            ...project,
            currentHoldingRatio: remaining,
            stage: '交割回款',
          },
        },
        transactions: {
          ...state.transactions,
          [transaction.id]: {
            ...transaction,
            soldRatio: action.soldRatio,
            actualClosingDate: action.closingDate,
            stage: '回款',
          },
        },
        activities: [
          activity('完成交易交割并更新剩余持股', 'transaction', transaction.id),
          ...state.activities,
        ],
      };
    }
    case 'RECORD_PAYMENT': {
      const plan = state.paymentPlans[action.paymentPlanId];
      const balance = plan.receivableAmount - plan.receivedAmount;
      if (action.amount <= 0 || action.amount > balance)
        throw new Error('到账金额不能超过应收余额');
      const receivedAmount = plan.receivedAmount + action.amount;
      const recordId = `record-${Date.now()}`;
      return {
        ...state,
        paymentPlans: {
          ...state.paymentPlans,
          [plan.id]: {
            ...plan,
            receivedAmount,
            status:
              receivedAmount === plan.receivableAmount ? '已回款' : '部分回款',
          },
        },
        paymentRecords: {
          ...state.paymentRecords,
          [recordId]: {
            id: recordId,
            paymentPlanId: plan.id,
            transactionId: plan.transactionId,
            amount: action.amount,
            paidDate: action.paidDate,
          },
        },
        activities: [
          activity(
            `回款到账 ${action.amount.toLocaleString()} 万元`,
            'payment',
            plan.id,
          ),
          ...state.activities,
        ],
      };
    }
    case 'UPDATE_TASK':
      return {
        ...state,
        tasks: {
          ...state.tasks,
          [action.taskId]: { ...state.tasks[action.taskId], ...action.changes },
        },
      };
    case 'ADD_TASK':
      return {
        ...state,
        tasks: { ...state.tasks, [action.task.id]: action.task },
      };
    case 'ADD_RISK':
      return {
        ...state,
        risks: { ...state.risks, [action.risk.id]: action.risk },
      };
    default:
      return state;
  }
};
